// P1 Phase E — the standard qualifier mechanism for design schedules.
//
// A qualification is a client-facing sentence that says which schedule
// values remain assumptions needing verification. Each has a STABLE KEY, a
// type from the Proposal Qualification Register vocabulary
// (docs/implementation/PROPOSAL_GENERATION.md), the publication levels it
// attaches at, and one approved text. Schedules REFERENCE keys (a schedule
// definition's `qualifications`); buildSchedule resolves them; the Design
// Schedules view prints every published qualification ONCE, deduplicated
// across schedules (the electrical sentence is shared by the Panel and
// Circuit schedules and appears one time), so a proposal never retypes them.
//
// This is the seed of the future DB-backed Proposal Qualification Register
// (LATER — Proposal Generation brief): a register row generated from a
// schedule will carry SourceType = 'schedule' and SourceID = the key below.
// Texts are product-owner approved wording or the previously shipped
// schedule sentences — never invent a new client commitment here; add a key
// with its approved text instead. Pure data, no React / DB.

export const QUALIFICATION_TYPES = ["Assumption", "Clarification", "Exclusion", "Allowance"];

// Product-owner wording (2026-09-12) for the Panel / Circuit schedules.
export const ELECTRICAL_PROPOSAL_QUALIFICATION = "Electrical panel and circuit information is based on the design criteria, assumed equipment loads, and information available during estimating. Connected loads, demand factors, breaker sizes, circuit assignments, feeder requirements, and final panel configuration are subject to verification and coordination during design development and prior to IFC issuance.";
// Product-owner wording (2026-09-12) for the Assumed Equipment schedule.
export const EQUIPMENT_PROPOSAL_QUALIFICATION = "Equipment selections, utility requirements, loads, dimensions and connections shown are based on information available during estimating and are subject to verification during design development and prior to IFC.";

export const SCHEDULE_QUALIFICATIONS = [
  { key: "design_criteria_basis",      type: "Assumption", title: "Design criteria basis",
    text: "Criteria not set room-by-room are the project's design basis and remain subject to verification." },
  { key: "hepa_estimate_stage",        type: "Assumption", title: "Terminal filtration quantities",
    text: "Filter quantities are estimate-stage design values from required airflow and ceiling coverage; final selection subject to engineering." },
  { key: "lighting_estimate_stage",    type: "Assumption", title: "Light fixture quantities",
    text: "Fixture quantities are estimate-stage design values from the target light level; final photometrics subject to engineering." },
  { key: "assumed_equipment",          type: "Assumption", title: "Assumed equipment",
    text: EQUIPMENT_PROPOSAL_QUALIFICATION },
  { key: "openings_layout",            type: "Assumption", title: "Doors, windows and pass-throughs",
    text: "Door, window and pass-through counts, sizes and locations shown are based on the estimating layout and are subject to verification during design development and prior to IFC." },
  { key: "electrical_panels_circuits", type: "Assumption", title: "Electrical panels and circuits",
    text: ELECTRICAL_PROPOSAL_QUALIFICATION },
  // Process Utilities Phase F (2026-09-12) — wording confirmed by the product
  // owner 2026-09-12.
  { key: "process_utilities_estimate_stage", type: "Assumption", title: "Process utilities",
    text: "Process utility demands, diversity factors, capacity margins, redundancy and plant requirements shown are based on assumed equipment connections and information available during estimating and are subject to verification and coordination during design development and prior to IFC issuance." },
  // System Architecture & Scenarios Phase J-3 (2026-09-13) — DRAFT wording,
  // pending product-owner approval; replace the text, keep the key.
  { key: "hvac_design_alternates", type: "Clarification", title: "HVAC design alternates",
    text: "The base HVAC design and each design alternate are conceptual system architectures based on estimate-stage loads; each alternate is priced as an add / deduct to the base design. Equipment selections, capacities and distribution are subject to verification during design development and prior to IFC issuance." },
].map(q => ({ levels: ["proposal", "dd"], ...q }));

export const QUALIFICATION_BY_KEY = Object.fromEntries(SCHEDULE_QUALIFICATIONS.map(q => [q.key, q]));

// Resolve a schedule's qualification keys for a level (unknown keys are
// dropped — the registry test pins that none exist).
export function qualificationsFor(keys = [], level) {
  return keys.map(k => QUALIFICATION_BY_KEY[k]).filter(q => q && q.levels.includes(level));
}

// One list across many built schedules, deduplicated by key, in first-seen
// order, each carrying the schedules that reference it.
export function collectQualifications(schedules = []) {
  const out = new Map();
  for (const s of schedules) {
    for (const q of s.qualifications || []) {
      const e = out.get(q.key) || { ...q, schedules: [] };
      if (!e.schedules.includes(s.key)) e.schedules.push(s.key);
      out.set(q.key, e);
    }
  }
  return [...out.values()];
}
